import styled from "@emotion/styled";
import React from "react";
import { useNavigate } from "react-router-dom";
import { i18n, mediaQuery, setGlobalLoading, showMessage, useAppDispatch } from "src/common";
import { CalculationResponseDto, GetOfferDto, useCreateHealthOfferMutation } from "src/common/api/apollo/additional";
import { Button, Divider, DropdownDownIcon, Flex, Loader, Text } from 'src/components';
import { AppRoutesAdditional } from "src/pages/app/_router/app.routes";
import { colors } from "src/theme";
import { Card } from "../card";
import { CoveragesAdditionalCard } from "./coveragesAdditional.card";
import { OfferAdditionalHeaderCard } from "./offerAdditionalHeader.part";


const OfferAdditionalCardStyle = styled(Card)({
  width: 'auto',
  maxWidth: '100%',
  minWidth: 0,
  boxSizing: 'border-box',

  [mediaQuery('laptopL')]: {
    marginLeft: 0,
    marginRight: 0
  },
});

const DetailsLink = styled.a({
  textDecoration: 'none',
  color: colors.primary,
  fontSize: '16px',
  lineHeight: '24px',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  gap: 8,
});


/* const DetailsLink = styled.div({
  textDecoration: 'none',
  color: colors.primary,
  fontSize: '16px',
  lineHeight: '24px',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  flexDirection: 'row',
  gap: 8,
  cursor: 'pointer',
}); */

const ButtonStyle = styled(Button)({
  [mediaQuery('laptopL')]: {
    minWidth: 200,
  },
});

/** Selfcare renewal: marks the package the customer already has (shown first). */
const HighlightBadge = styled.div({
  alignSelf: 'flex-start',
  padding: '4px 10px',
  borderRadius: 999,
  backgroundColor: colors.primary,
  color: '#fff',
  fontSize: 12,
  fontWeight: 700,
  lineHeight: '16px',
  textTransform: 'uppercase',
});

export interface CalculationAdditionalCardProps {
  calculation: CalculationResponseDto;
  loading?: boolean;
  className?: string;
  showSelectButton?: boolean;
  productTypeCode?: string;
  source?: string;
  onSelectOffer?: (offer: GetOfferDto) => void;
  selectButtonText?: string;
  offerSubtitle?: string;
  offerSubDescription?: string;
  soId: string;
  /** Badge above the header, e.g. "Vaš trenutni paket" on a renewal. */
  highlightLabel?: string;
}

export const CalculationAdditionalCard = ({
  calculation,
  loading,
  className,
  showSelectButton = true,
  selectButtonText = 'Kupi',
  offerSubDescription,
  soId,
  highlightLabel
}: CalculationAdditionalCardProps) => {
  // const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
  const [createHealthOfferMutation] = useCreateHealthOfferMutation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  if (loading) {
    return (
      <Card radius={12} padding={20} className={className}>
        <Loader />
      </Card>
    )
  }

  const monthlyPrice = (calculation.prices.generalTransferWithInstallments || calculation.prices.creditCardWithInstallments || 0) / 12;
  const yearlyPrice = calculation.prices.creditCard ?? 0;

  const handleSelectOffer = () => {
    dispatch(setGlobalLoading(true));
    createHealthOfferMutation({
      variables: {
        id: soId,
        input: {
          productCode: calculation.code,
          coverages: [],
        }
      },
      onCompleted: (data) => {
        navigate(`${AppRoutesAdditional.HEALTH_QUESTIONNAIRE.fullPath}/${data.createHealthOffer}`);
      },
      onError: () => {
        dispatch(showMessage({ message: i18n.t('common.globalError'), type: 'error', duration: 5000 }));
      }
    }).finally(() => {
      dispatch(setGlobalLoading(false));
    });
  };

  const detailsUrl = calculation.detailsLink ?? '';

  return (
    <OfferAdditionalCardStyle padding={8} className={className} >
      <Flex vertical gap={12}>
        {highlightLabel && <HighlightBadge>{highlightLabel}</HighlightBadge>}
        <OfferAdditionalHeaderCard
          monthlyPrice={monthlyPrice}
          yearlyPrice={yearlyPrice}
          priceLabel="/12 mj."
          logoUrl={calculation.logoUrl ?? ''}
          offerSubtitle={calculation.label ?? ''}
          offerSubDescription={offerSubDescription ?? "Za osobe od 18 do 70 godina"}
        />
        <Divider dashed />
        <Flex vertical gap={4}>
          <Text type="tiny" color={colors.text4}>{i18n.t('pages.offersAdditionalPage.addedDescription')}</Text>
          <CoveragesAdditionalCard isDetailsOpen={false} coverages={calculation.coverages?.map((coverage) => ({ code: coverage.code, label: coverage.label ?? '', description: coverage.description ?? '' })) ?? []} fixedCoverages={calculation.fixedCoverages?.map((coverage) => ({ code: coverage.code, label: coverage.label ?? '', description: coverage.description ?? '' })) ?? []} />
        </Flex>
        <Flex justify='space-between' gap={8}>
          {detailsUrl && (
            <DetailsLink href={detailsUrl ?? ''} target="_blank" rel="noopener noreferrer">
              <Text type="base" bold color={colors.primary}>{i18n.t('pages.offersAdditionalPage.showDetails')}</Text>
              <DropdownDownIcon color={colors.primary} />
            </DetailsLink>
          )}
          {/* <DetailsLink onClick={() => setIsDetailsOpen(!isDetailsOpen)}>
            <Text type="base" bold color={colors.primary}>{isDetailsOpen ? i18n.t('pages.offersAdditionalPage.hideDetails') : i18n.t('pages.offersAdditionalPage.showDetails')}</Text>
            <DropdownDownIcon color={colors.primary} isOpen={isDetailsOpen} />
          </DetailsLink> */}
          {showSelectButton && <ButtonStyle
            btnType="primary"
            onClick={handleSelectOffer}
            text={selectButtonText}
          />}
        </Flex>
      </Flex>
    </OfferAdditionalCardStyle>
  )
};
